import { Routes } from '@angular/router'
import { ROUTE_TITLES } from './core/branding/route-titles'
import { environment } from '../environments/environment'
import { effectiveSentryWebEnv } from './core/error/runtime-sentry-env'
import { authGuard } from './core/auth/auth.guard'
import { roleGuard } from './core/auth/role.guard'
import { rootRedirectGuard } from './core/auth/root-redirect.guard'
import { authRoutes } from './routes/auth.routes'
import { brandedAddressRoutes, neutralAddressGuard } from './routes/branded-address.routes'
import { BRANDED_ADDRESS_PREFIX } from './core/branding/branded-address'
import {
  agencyHostMatch,
  confirmedPlatformMatch,
  hostNotActiveMatch,
  hostUnavailableMatch,
} from './core/branding/host-routing'
import { neutralLanguageGuard } from './core/i18n/pre-login-language'

const baseRoutes: Routes = [
  { path: '', pathMatch: 'full', canActivate: [rootRedirectGuard], children: [] },
  ...authRoutes,
  {
    // FR-023 REQ-2 (U-2c) — the PUBLIC, UNGUARDED banner-share preview. A sibling of
    // the auth-flow public pages (accept-invitation, reset-password), OUTSIDE the
    // portal/admin shells and with NO guard. The `:token` param matches ANY string, so
    // a malformed token still activates this route and the component renders the "no
    // longer available" state — it is NEVER swallowed by the catch-all →
    // rootRedirectGuard → /login bounce that guards an unauthenticated visitor (AC-6).
    path: 'p/banner/:token',
    // FR-052 U2 (AC-1.9) — branded tab: "Banner preview · <display name>".
    title: ROUTE_TITLES.bannerPreview,
    loadComponent: () =>
      import('./features/public-banner-share/public-banner-share.page').then(
        (m) => m.PublicBannerSharePage
      ),
  },
  {
    // FR-045 REQ-3 (U5) — the PUBLIC, UNGUARDED, LOGGED-OUT campaign-stats share
    // page. A sibling of the banner-share page, OUTSIDE the portal/admin shells and
    // with NO guard. The exact path `p/campaign-stats/:token` must match U4's Caddy
    // `/p/campaign-stats/*` frame-ancestors block. The `:token` param matches ANY
    // string, so a malformed token still activates this route and the component
    // renders the "no longer available" state — it is NEVER swallowed by the
    // catch-all → rootRedirectGuard → /login bounce (AC-R.1 / AC-3.7).
    path: 'p/campaign-stats/:token',
    // FR-052 U2 (AC-1.3) — branded tab: "Campaign statistics · <display name>".
    title: ROUTE_TITLES.campaignStatistics,
    loadComponent: () =>
      import('./features/public-campaign-stats/public-campaign-stats.page').then(
        (m) => m.PublicCampaignStatsPage
      ),
  },
  {
    // FR-075 REQ-6 (AC-6.1) — the PUBLIC, UNGUARDED privacy policy. A sibling of the
    // share pages, OUTSIDE the portal/admin shells and with NO guard, so a signed-out
    // visitor (and the Meta app review) reads it instead of being bounced to /login.
    // On an agency host BrandService paints the host brand as for any page (AC-6.3).
    path: 'privacy',
    title: ROUTE_TITLES.privacy,
    loadComponent: () => import('./features/privacy/privacy.page').then((m) => m.PrivacyPage),
  },
  {
    path: 'select-account',
    title: ROUTE_TITLES.selectAccount,
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/select-account/select-account.page').then((m) => m.SelectAccountPage),
  },
  {
    path: 'no-access',
    title: ROUTE_TITLES.noAccess,
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/no-access/no-access.page').then((m) => m.NoAccessPage),
  },
  {
    path: 'admin',
    // FR-051 U7 (AC-5.5 web) + fix F-A: the admin realm opens only on a host the
    // server CONFIRMED is the platform's — never on an agency host (agencyHostAdmin
    // below), never after a failed host context (the unavailable gate).
    canMatch: [confirmedPlatformMatch],
    canActivate: [authGuard, roleGuard('admin')],
    loadComponent: () =>
      import('./core/layout/admin-shell.component').then((m) => m.AdminShellComponent),
    // #4178 — the route trees carry route-`providers` services; loadChildren keeps them
    // (and the wizard tree) out of the initial bundle. Only `canMatch` runs before the
    // chunk is fetched — `canActivate` runs after it (the code was in main for every
    // visitor before, so nothing new is exposed).
    loadChildren: () => import('./routes/admin.routes').then((m) => m.adminRoutes),
  },
  {
    path: 'portal',
    canActivate: [authGuard, roleGuard('customer', 'admin')],
    loadComponent: () =>
      import('./core/layout/portal-shell.component').then((m) => m.PortalShellComponent),
    loadChildren: () => import('./routes/portal.routes').then((m) => m.portalRoutes),
  },
]

// FR-075 AC-5.4: gated on the EFFECTIVE environment (the host's /runtime-config.js over
// the baked one) — production serves the bundle staging built, which baked `staging`.
const debugRoutes: Routes =
  effectiveSentryWebEnv(environment).SENTRY_ENV !== 'production'
    ? [
        {
          path: 'sentry-debug',
          loadComponent: () =>
            import('./__debug/sentry-debug.component').then((m) => m.SentryDebugComponent),
        },
      ]
    : []

// FR-050 R2-B (finding 4): every branded address lives under `/a/<name>`, so the
// `a` segment is the ONLY first segment branding owns (CI:
// scripts/check-reserved-brand-names.mjs). Everything under it is a branded page
// or a not-found; it never falls through to the catch-all.
const brandedAddress: Routes = [{ path: BRANDED_ADDRESS_PREFIX, children: brandedAddressRoutes }]

// Catch-all must come last so the debug route (and any future routes) match first.
// FR-050 AC-6.5: the old top-level `/<name>` is an unknown path like any other —
// today's catch-all, no redirect (a redirect only for active names would let
// anyone enumerate them).
const catchAll: Routes = [{ path: '**', redirectTo: '' }]

// FR-051 U7 (spec §3.6) — gates by the host the app is served on, ahead of every
// other route. On a platform host none matches (canMatch): FR-050's table is
// unchanged. No `matcher` routes at the top level (app.routes.spec.ts, R2-B).
const neutralNotFoundOnAgencyHost: Routes = [
  {
    path: '**',
    canActivate: [neutralLanguageGuard],
    title: ROUTE_TITLES.notFound,
    loadComponent: () =>
      import('./features/not-found/neutral-not-found.page').then((m) => m.NeutralNotFoundPage),
  },
]
const hostGates: Routes = [
  // AC-4.2 / AC-4.6: an unknown or switched-off host renders ONE page for every
  // path (`**` matches `/` too), URL kept.
  {
    path: '**',
    canMatch: [hostNotActiveMatch],
    canActivate: [neutralAddressGuard, neutralLanguageGuard],
    title: ROUTE_TITLES.notActive,
    loadComponent: () =>
      import('./features/not-found/host-not-active.page').then((m) => m.HostNotActivePage),
  },
  // Fix F-A (fail closed): the host context could not be read (after one retry) —
  // this may be an agency host, so no platform flow renders: one neutral
  // "temporarily unavailable" page with a reload, for every path, URL kept.
  {
    path: '**',
    canMatch: [hostUnavailableMatch],
    canActivate: [neutralLanguageGuard],
    title: ROUTE_TITLES.unavailable,
    loadComponent: () =>
      import('./features/not-found/host-unavailable.page').then((m) => m.HostUnavailablePage),
  },
  // AC-5.5 (web): no self-registration on an agency host — FR-050's neutral not-found.
  { path: 'register', canMatch: [agencyHostMatch], children: neutralNotFoundOnAgencyHost },
]

// AC-5.5 (web): `/admin` and `/admin/**` on an agency host — the admin shell above
// does not match there (confirmedPlatformMatch), so this neutral not-found does. After
// baseRoutes so the shell stays THE `admin` route of the table.
const agencyHostAdmin: Routes = [
  { path: 'admin', canMatch: [agencyHostMatch], children: neutralNotFoundOnAgencyHost },
]

export const routes: Routes = [
  ...hostGates,
  ...baseRoutes,
  ...agencyHostAdmin,
  ...debugRoutes,
  ...brandedAddress,
  ...catchAll,
]
